import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as mobxReact from 'mobx-react';
import classnames from 'classnames';
import { AttendeeColumn } from '../../../../../global/attendee-column/attendee-column';
import { IControlMeetingDetailsDTO } from '../../../../../../api/control.dao';
import { ProgressDotIndicator } from '../../../../../global/progress-dot-indicator/progress-dot-indicator';
import './control-list-item.scss';
import { IOperatorDetailsDTO } from '../../../../../../api/rooms.dao';
import { FormattedMessage, useIntl } from 'react-intl';
import OperatorColumn from '../../../../../global/operator-column/operator-column';
import DefaultButton from '../../../../../global/default-button/default-button';
import NoteIcon from '../../../../../../images/svg/icons8-comments';
import { Popover } from '../../../../../global/popover/popover';
import { ExpandArrowDownIcon, ExpandArrowUpIcon } from '../../../../../global/icons/icons';
import Text from '../../../../../global/text/text';
import ControlViewModel from '../../control.viewmodel';
import { Permission } from '../../../../../../api/roles.dao';
import { MeetingStatus } from '../../../../../../api/meetings.dao';
import { hasPermission } from '../../../../../../infrastructure/utils/has-permissions';
import { GroupedOperators } from '../../../../../global/operator-column/grouped-operators';
import { getCompleteRoomName } from '../../../../../../infrastructure/utils/common.utils';
import { MAX_SAFE_TIMEOUT_DELAY, TIME_TO_HIGHLIGHT_INCOMPLETE_ATTENDEE } from '../../../../../../constants/common.constants';
import { stringifyStaffMembers } from '../../../../../../infrastructure/utils/users.utils';
import { ControlListItemConnections } from './_control-list-item-connections';
import MeetingBadgeContent from './meeting-badge-content';
import {
    formatPopoverData,
    GENERATE_REPORT_BUTTON_LABEL,
    getCountOfAttendeesWithIssue,
    INTERACTIONS_REPORT_LABEL,
    REGISTRATION_REPORT_LABEL
} from './_control-list-item-utils';
import { OpenFrom } from '../../../../../../tracking-service/tracking-values';
import { ConnectionType } from '../../../../../../api/connections.dao';
import AttendeeJoinedCounter from '../_attendee-joined-counter/attendee-joined-counter';
import { IAttendeeOrganizationDTO } from '../../../../../../api/attendees.dao';
import { getSortedConnections } from '../../../../../../infrastructure/utils/connections.utils';
import OptionsButton from '../../../../../global/options-button/options-button';

interface IProps extends IControlMeetingDetailsDTO {
    onCollapseToggle: (meetingId: number) => void;
    isCollapsed: boolean;
    eventTimezone: string;
    isFirstInTimeGroup: boolean;
    isVisible: boolean;
    controlVM: ControlViewModel;
    groupedOperators: { [path: string]: IOperatorDetailsDTO[] };
    showColumn: (id) => boolean;
    generateMeetingInteractionReport?: (meetingId: number) => void;
    isTypesForMeetingsWithCounters?: boolean;
}

const MeetingBadge = mobxReact.observer(({ isNotesExist, attendeeIssuesCount }) => {
    if (isNotesExist) {
        return <NoteIcon />;
    } else if (attendeeIssuesCount) {
        return (<div className='meeting-status-column__container__issues'>
            {attendeeIssuesCount}
        </div>);
    }
    return null;
});

const ControlListItem = mobxReact.observer((props: IProps) => {
    const {
        meetingId,
        defaultConnectionId,
        presenters,
        participants,
        hosts,
        room,
        progress,
        status,
        type,
        eventTimezone,
        operatorMeetingNotes,
        onCollapseToggle,
        isCollapsed,
        isFirstInTimeGroup,
        isVisible,
        controlVM,
        operators,
        groupedOperators,
        showColumn,
        total,
        connectionVersion,
        registrantsCount,
        startDateTime,
        topic,
        podiumLinkEnabled,
    } = props;

    const meetingConnections = props.connections;
    const meetingIdAsString = meetingId.toString();

    const {
        searchQuery,
        handleRunClick,
        openAttendeesLivePopup,
        subscribeMeeting,
        unsubscribeMeeting,
        isMeetingRunFormShown,
        getEvent,
        getAccountId,
        getMeetingRunTimeInMilliseconds,
        generateMeetingInteractionsReport,
        generateMeetingRegistrationReport,
        isParticipantsFormShown,
    } = controlVM;

    const intl = useIntl();
    const meetingRunTime = getMeetingRunTimeInMilliseconds(meetingId);
    const [isMeetingUnderway, setMeetingUnderway] = useState(meetingRunTime >= TIME_TO_HIGHLIGHT_INCOMPLETE_ATTENDEE);
    const highlightDelay = TIME_TO_HIGHLIGHT_INCOMPLETE_ATTENDEE - meetingRunTime;
    useEffect(() => {
        if (!isMeetingUnderway && highlightDelay < MAX_SAFE_TIMEOUT_DELAY) {
            const timeout = setTimeout(() => {
                setMeetingUnderway(true);
            }, highlightDelay);

            return () => {
                clearTimeout(timeout);
            };
        }
    }, []);

    const isSelected = isMeetingRunFormShown(meetingId);
    const isParticipantsPopoverOpen = isParticipantsFormShown(meetingId);
    const eventId = getEvent().eventId;
    const accountId = getAccountId();

    const [isBadgePopoverOpen, setBadgePopover] = useState(false);
    const [isControlListItemHovered, setIsControlListItemHovered] = useState(false);
    const badgeRef = useRef(null);

    const controlListItemClass = classnames('control-list-item', {
        'control-list-item--cancelled': status === MeetingStatus.Cancelled,
        'control-list-item--first': isFirstInTimeGroup,
        'control-list-item--collapsed': isCollapsed
    });

    const reportGenerationNotificationProps = {
        startDateTime: startDateTime,
        topic: topic,
        presentingOrganizationNames: props.presenters?.map(org => org.name),
    };

    const reportOptions = [
        hasPermission(Permission.MEETING_REGISTRATION_REPORT) && {
            label: intl.formatMessage(REGISTRATION_REPORT_LABEL),
            action: () => generateMeetingRegistrationReport(meetingId, reportGenerationNotificationProps)
        },
        hasPermission(Permission.MEETING_INTERACTIONS_REPORT) && {
            label: intl.formatMessage(INTERACTIONS_REPORT_LABEL),
            action: () => generateMeetingInteractionsReport(meetingId, reportGenerationNotificationProps)
        },
    ].filter(Boolean);

    const cellEffectClasses = classnames({
        active: isSelected,
        hoverable: !isSelected,
    });

    const onRunClick = () => {
        if (!isBadgePopoverOpen) handleRunClick(meetingId);
    };

    const getAttendeeSelectorCount = (selector: string): number => {
        let result = 0;
        [presenters, participants, hosts].forEach(item => {
            const listOfAttendees = item.map(org => org.attendees);
            result += getCountOfAttendeesWithIssue([].concat(...listOfAttendees), selector);
        });

        return result;
    };

    const getBadgePopoverData = () => {
        const result = [];
        [presenters, participants, hosts].forEach(item => {
            const listOfAttendees = item.map(org => ([{ organizationName: org.name, attendees: org.attendees }]));
            formatPopoverData([].concat(...listOfAttendees), result);
        });

        return result;
    };
    const chaperones = [...presenters, ...participants, ...hosts].map(org => org.chaperones).reduce((acc, curr) => [...acc, ...(curr || [])], []);
    const formattedChaperones = [...new Set(stringifyStaffMembers(chaperones))].sort((a, b) => a.localeCompare(b));

    const attendeeIssuesCount = getAttendeeSelectorCount('issues');
    const attendeeNotesCount = getAttendeeSelectorCount('meetingAttendeeNote');
    const isNotesExist = !attendeeIssuesCount && (attendeeNotesCount || operatorMeetingNotes);
    const popoverData = getBadgePopoverData();

    const issuesContainerClassName = classnames('meeting-status-column__container', {
        'active': isBadgePopoverOpen,
        'meeting-status-column__container__with-issues': attendeeIssuesCount,
        'meeting-status-column__container__with-notes': isNotesExist
    });

    const hasAttendeeLiveData = total !== null;
    const isTypesForMeetingsWithCounters = meetingConnections.find(el =>
        el.type === ConnectionType.Knovio ||
        el.type === ConnectionType.Webinar ||
        (el.type === ConnectionType.Zoom && (el.autoSyncEnabled || el.autoRegistrationEnabled))
    );

    const totalParticipantAttendeeCount = useMemo(() => participants.reduce((acc: number, organization: IAttendeeOrganizationDTO) => {
        return acc + organization.attendees.length;
    }, 0), [participants]);

    const shouldRenderParticipantAttendeeCounter = useMemo(() => totalParticipantAttendeeCount > 10 &&
        !isTypesForMeetingsWithCounters,
        [totalParticipantAttendeeCount, meetingConnections]);

    const badgePopoverHandler = () => {
        setBadgePopover(true);
    };

    const closeBadgePopover = (e?: React.ChangeEvent<HTMLInputElement>) => {
        if (e) e.stopPropagation();
        setBadgePopover(false);
    };

    const getRecordMeetingText = (currentMeetingId: number): JSX.Element | null => {
        const currentMeeting = controlVM.meetingsList.find(meeting => meeting.meetingId === currentMeetingId);

        if (currentMeeting && currentMeeting.meetingRecordEnabled) {
            return (
                <div className='record-meeting'>
                    <div className='record-meeting__dot-wrapper'>
                        <div className='record-meeting__dot'></div>
                    </div>
                    <div className='record-meeting__text'>
                        <FormattedMessage
                            id='oe.control.recordMeeting'
                            defaultMessage='Record Meeting'
                            description='Label text'
                        />
                    </div>
                </div>
            );
        }

        return null;
    };

    useEffect(() => {
        let subscriptions = [];

        if (isVisible) {
            subscribeMeeting(meetingId, room?.roomId).then(_subscriptions => {
                subscriptions = _subscriptions;
            });

        } else {
            unsubscribeMeeting(subscriptions);
        }
        return () => {
            unsubscribeMeeting(subscriptions);
        };
    }, [isVisible]);

    return (
        <tr className={controlListItemClass}
            onDoubleClick={hasPermission(Permission.RUN_MEETING) && status !== MeetingStatus.Cancelled && onRunClick}
            onMouseEnter={() => setIsControlListItemHovered(true)}
            onMouseLeave={() => setIsControlListItemHovered(false)}
        >
            <td className={cellEffectClasses}>
                <div className='meeting-status-column'>
                    <div className='collapse-icon' onClick={() => onCollapseToggle(meetingId)}>
                        {isCollapsed ? <ExpandArrowDownIcon /> : <ExpandArrowUpIcon />}
                    </div>
                    {(attendeeIssuesCount || isNotesExist) &&
                        <div
                            className={issuesContainerClassName}
                            onClick={badgePopoverHandler}
                            ref={badgeRef}
                        >
                            <MeetingBadge isNotesExist={isNotesExist} attendeeIssuesCount={attendeeIssuesCount} />
                            <Popover
                                buttonRef={badgeRef}
                                isOpen={isBadgePopoverOpen}
                                position='right'
                                align='end'
                                onClickOutside={closeBadgePopover}
                                withCloseIcon
                                onClose={closeBadgePopover}
                            >
                                <MeetingBadgeContent popoverData={popoverData} operatorMeetingNotes={operatorMeetingNotes} />
                            </Popover>
                        </div>}
                </div>
            </td>
            {showColumn('status') &&
                <td className={cellEffectClasses}>
                    <ProgressDotIndicator currentProgress={progress} />
                </td>
            }
            {showColumn('presenters') &&
                <td className={cellEffectClasses}>
                    <AttendeeColumn
                        organizations={presenters}
                        withAttendeeStatus
                        searchValue={searchQuery}
                        isCollapsed={isCollapsed}
                        eventId={eventId}
                        accountId={accountId}
                        withAttendeePopover
                        withLiveStatus
                        isMeetingUnderway={isMeetingUnderway}
                        attendeeDetailsPopoverVM={controlVM.getAttendeeDetailsPopoverVM()}
                        additionalAttendeeId={meetingIdAsString}
                        openFrom={OpenFrom.Control}
                    />
                </td>
            }
            {showColumn('participants') &&
                <td className={cellEffectClasses}>
                    {shouldRenderParticipantAttendeeCounter &&
                        <AttendeeJoinedCounter
                            organizations={participants}
                            isCollapsed={isCollapsed}
                            totalCount={totalParticipantAttendeeCount}
                        />
                    }
                    <div className={classnames('attendee-counter-block cursor-point attendee-stats',
                        {
                            'attendee-counter-block--active': isParticipantsPopoverOpen,
                            'attendee-counter-block--select-item': !isParticipantsPopoverOpen && (isControlListItemHovered || isSelected),
                            'attendee-counter-block--hidden': !(hasAttendeeLiveData && isTypesForMeetingsWithCounters) && registrantsCount <= 0,
                        }
                    )}
                        onClick={() => openAttendeesLivePopup({ ...props, isTypesForMeetingsWithCounters: !!isTypesForMeetingsWithCounters })}>
                        {hasAttendeeLiveData && isTypesForMeetingsWithCounters &&
                            <p>
                                <strong>{total}</strong>
                                &nbsp;
                                <FormattedMessage
                                    id='oe.common.joined'
                                    defaultMessage='Joined'
                                    description='Label text'
                                />
                            </p>
                        }
                        {!isCollapsed && registrantsCount > 0 &&
                            <div className='attendee-registrants'>
                                <p>
                                    <strong>{registrantsCount}</strong>
                                    &nbsp;
                                    <FormattedMessage
                                        id='oe.common.registrants'
                                        defaultMessage='Registrants'
                                        description='Label text'
                                    />
                                </p>
                            </div>
                        }
                    </div>

                    {!(shouldRenderParticipantAttendeeCounter && isCollapsed) &&
                        <AttendeeColumn
                            data-testid='participant-attendees-column'
                            organizations={participants}
                            withAttendeeStatus
                            searchValue={searchQuery}
                            isCollapsed={isCollapsed}
                            eventId={eventId}
                            accountId={accountId}
                            withAttendeePopover
                            withLiveStatus
                            isMeetingUnderway={isMeetingUnderway}
                            attendeeDetailsPopoverVM={controlVM.getAttendeeDetailsPopoverVM()}
                            additionalAttendeeId={meetingIdAsString}
                            openFrom={OpenFrom.Control}
                        />
                    }
                </td>
            }
            {showColumn('hosts') &&
                <td className={cellEffectClasses}>
                    <AttendeeColumn
                        organizations={hosts}
                        withAttendeeStatus
                        searchValue={searchQuery}
                        isCollapsed={isCollapsed}
                        eventId={eventId}
                        accountId={accountId}
                        withAttendeePopover
                        withLiveStatus
                        isMeetingUnderway={isMeetingUnderway}
                        attendeeDetailsPopoverVM={controlVM.getAttendeeDetailsPopoverVM()}
                        additionalAttendeeId={meetingIdAsString}
                        openFrom={OpenFrom.Control}
                    />
                </td>
            }
            {
                showColumn('type') &&
                <td className={`${cellEffectClasses} column-with-filters`}>
                    <Text message={type} withEllipsis />
                </td>
            }
            {
                showColumn('operators') &&
                <td className={`${cellEffectClasses} column-with-filters`}>
                    {isCollapsed ? (
                        <>
                            <OperatorColumn operators={room?.operators || []} withGreyedCheckMark={true} isCollapsed={isCollapsed} />
                            <OperatorColumn operators={operators || []} withGreyedCheckMark={true} isCollapsed={isCollapsed} />
                        </>
                    ) : (
                        <GroupedOperators groupedOperators={groupedOperators} withGreyedCheckMark={true} meetingId={meetingId} />
                    )}
                </td>
            }
            {
                showColumn('chaperone') &&
                <td className={`${cellEffectClasses} column-with-filters`}>
                    <div title={formattedChaperones.join('\n')}>
                        {formattedChaperones.map((person, index) => {
                            if (index <= 1) return <Text key={person} message={person} withEllipsis withoutTitle />;
                        })}
                    </div>
                </td>
            }
            {
                showColumn('connections') &&
                <td className={cellEffectClasses}>
                    {isCollapsed ? (
                        <div className='control-list-item__room-wrapper'>
                            {getRecordMeetingText(meetingId)}
                            {room?.roomName && room.connections.length > 0 &&
                                <Text
                                    tag='div'
                                    data-testid='control-list-item-room'
                                    className={classnames('control-list-item__text', {
                                        'control-list-item__text--collapsed': isCollapsed
                                    })}
                                    message={(getRecordMeetingText(meetingId) ? ', ' : '') +
                                        getCompleteRoomName({ name: room.roomName, label: room.roomLabel })}
                                    withEllipsis
                                />}
                        </div>
                    ) : (
                        <ControlListItemConnections
                            controlVM={controlVM}
                            room={room}
                            meetingId={meetingId}
                            eventTimezone={eventTimezone}
                            connections={getSortedConnections(meetingConnections)}
                            defaultMeetingConnectionId={defaultConnectionId}
                            webinarReportsVM={controlVM.getWebinarReportsVM()}
                            getRecordMeetingText={getRecordMeetingText}
                            connectionVersion={connectionVersion}
                            podiumLinkEnabled={podiumLinkEnabled}
                            handlePodiumLinkFailure={controlVM.handlePodiumLinkFailure}
                            trackCopyPodiumLinkAction={controlVM.trackCopyPodiumLinkAction}
                        />
                    )}
                </td>
            }
            <td className={cellEffectClasses} />
            <td className={classnames('align-right', cellEffectClasses)}>
                <div className='flex column items-right'>
                    {hasPermission(Permission.RUN_MEETING) && <DefaultButton
                        className='visible-while-hovered run-meeting-button'
                        modifier='slim'
                        text={<FormattedMessage
                            id='oe.common.run'
                            defaultMessage='Run'
                            description='Button label text'
                        />}
                        onClick={onRunClick}
                    />}
                    {!!reportOptions.length && <OptionsButton
                        className='visible-while-hovered generate-report-button'
                        modifier={['slim', 'options']}
                        label={intl.formatMessage(GENERATE_REPORT_BUTTON_LABEL)}
                        options={reportOptions}
                    />}
                </div>
            </td>
        </tr >
    );
});

export { ControlListItem, IProps as IControlListItemProps, MeetingBadge };
